/**
 * Google Analytics 4 (GA4) Telemetry Engine
 * 
 * Lightweight, zero-dependency tracking layer designed for SPAs.
 * Safe for all environments (SSR, Jest tests, local development without ID).
 */

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

let isInitialized = false;

/**
 * Initialize GA4 tracking script dynamically
 */
export const initGA = () => {
    if (typeof window === 'undefined' || isInitialized) return;
    if (!GA_MEASUREMENT_ID) {
        if (process.env.NODE_ENV === 'development') {
            console.info('[Analytics] REACT_APP_GA_MEASUREMENT_ID is not set. Telemetry running in dry-run mode.');
        }
        return;
    }

    try {
        // Prevent duplicate injection
        const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`);
        if (!existingScript) {
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
            document.head.appendChild(script);
        }

        window.dataLayer = window.dataLayer || [];
        function gtag() {
            window.dataLayer.push(arguments);
        }
        window.gtag = gtag;

        gtag('js', new Date());
        gtag('config', GA_MEASUREMENT_ID, {
            send_page_view: false, // Managed manually for SPAs
            anonymize_ip: true,
        });

        isInitialized = true;
    } catch (err) {
        console.warn('[Analytics] Failed to initialize Google Analytics:', err.message);
    }
};

/**
 * Record a pageview in Google Analytics
 * @param {string} path 
 * @param {string} title 
 */
export const trackPageView = (path = window.location.pathname, title = document.title) => {
    if (typeof window === 'undefined') return;

    if (process.env.NODE_ENV === 'development') {
        console.log(`📊 [Analytics PageView] ${path} - "${title}"`);
    }

    if (window.gtag && GA_MEASUREMENT_ID) {
        window.gtag('event', 'page_view', {
            page_path: path,
            page_title: title,
            page_location: window.location.href,
        });
    }
};

/**
 * Record a custom interaction or conversion event
 * @param {string} eventName 
 * @param {Object} params 
 */
export const trackEvent = (eventName, params = {}) => {
    if (typeof window === 'undefined') return;

    if (process.env.NODE_ENV === 'development') {
        console.log(`🎯 [Analytics Event] ${eventName}`, params);
    }

    if (window.gtag && GA_MEASUREMENT_ID) {
        window.gtag('event', eventName, {
            ...params,
            timestamp: new Date().toISOString(),
        });
    }
};
